import { useRef, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useResetPasswordRequestMutation } from './authApiSlice'
import PulseLoader from 'react-spinners/PulseLoader'
import useTitle from '../../hooks/useTitle'
import { useTranslation } from 'react-i18next'
import AuthShell from '../../components/AuthShell'

const ForgotPassword = () => {
    const { t } = useTranslation()
    useTitle(t('titles.forgotPassword'))

    const emailRef = useRef()
    const errRef = useRef()

    const [email, setEmail] = useState('')
    const [errMsg, setErrMsg] = useState('')
    const [successMsg, setSuccessMsg] = useState('')

    const [resetPasswordRequest, { isLoading }] = useResetPasswordRequestMutation()

    useEffect(() => {
        emailRef.current?.focus()
    }, [])

    useEffect(() => {
        setErrMsg('')
    }, [email])

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            await resetPasswordRequest({ email }).unwrap()
            setSuccessMsg(t('auth.forgot.sent', { email }))
            setEmail('')
        } catch (err) {
            if (!err.status) {
                setErrMsg(t('auth.errors.noServerResponse'))
            } else if (err.status === 404) {
                setErrMsg(t('auth.forgot.errors.notFound'))
            } else {
                setErrMsg(err.data?.message || t('auth.forgot.errors.failed'))
            }
            errRef.current.focus()
        }
    }

    const errClass = errMsg ? 'errmsg' : 'offscreen'

    if (isLoading) return <PulseLoader color={'#e8c56d'} />

    return (
        <AuthShell
            eyebrow={t('auth.eyebrow')}
            title={t('auth.forgot.heading')}
            subtitle={successMsg ? undefined : t('auth.forgot.subtitle')}
            links={[
                { to: '/login', label: t('auth.backToSignIn') },
                { to: '/', label: t('auth.backToHome') },
            ]}
        >
            <p ref={errRef} className={errClass} aria-live="assertive" tabIndex={-1}>{errMsg}</p>

            {successMsg ? (
                <div className="auth__body" aria-live="polite">
                    <p className="successmsg">{successMsg}</p>
                    <div className="auth__actions">
                        <Link to="/reset-password" className="btn">{t('auth.forgot.enterCode')}</Link>
                    </div>
                </div>
            ) : (
                <form className="form" onSubmit={handleSubmit}>
                    <div className="auth__field">
                        <label className="form__label" htmlFor="email">{t('auth.email')}</label>
                        <input
                            className="form__input"
                            type="email"
                            id="email"
                            ref={emailRef}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            required
                        />
                    </div>

                    <button className="form__submit-button">{t('auth.forgot.submit')}</button>
                </form>
            )}
        </AuthShell>
    )
}

export default ForgotPassword
